// utils/generatePassword.ts
//
// Genera una contraseña aleatoria fuerte para que el admin pueda asignarla a
// un aseador/cliente sin tener que inventarla. Usa crypto.getRandomValues
// (CSPRNG del navegador) en vez de Math.random, que no es apto para secretos.
//
// Excluimos caracteres ambiguos (O/0, l/1/I) para que sea fácil de leer y
// dictar por teléfono. Garantizamos al menos un carácter de cada clase
// (minúscula, mayúscula, dígito, símbolo) para cumplir validadores comunes.

const LOWER = "abcdefghijkmnpqrstuvwxyz"; // sin l
const UPPER = "ABCDEFGHJKLMNPQRSTUVWXYZ"; // sin I, O
const DIGITS = "23456789"; // sin 0, 1
const SYMBOLS = "!@#$%*?";
const ALL = LOWER + UPPER + DIGITS + SYMBOLS;

/** Índice aleatorio uniforme en [0, max) usando el CSPRNG del navegador. */
function randomIndex(max: number): number {
  const arr = new Uint32Array(1);
  crypto.getRandomValues(arr);
  return arr[0] % max;
}

const pick = (set: string): string => set[randomIndex(set.length)];

/**
 * Devuelve una contraseña aleatoria de `length` caracteres (default 14).
 * Siempre incluye al menos 1 minúscula, 1 mayúscula, 1 dígito y 1 símbolo.
 */
export function generatePassword(length = 14): string {
  const len = Math.max(8, length); // nunca menos de 8
  const chars: string[] = [pick(LOWER), pick(UPPER), pick(DIGITS), pick(SYMBOLS)];
  for (let i = chars.length; i < len; i++) chars.push(pick(ALL));
  // Fisher-Yates para no dejar las clases garantizadas siempre al inicio.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join("");
}
